import { useMutation } from '@apollo/client'
import { createSearchParams, useSearchParams } from 'react-router-dom'
import { BLOG_POST_MEDIA_PATH_S3 } from 'constants/images'
import { getTableOfContent, validateTableOfContent } from 'components/TableOfContent'
import withPlatformCheck from 'HOCs/withPlatformCheck'
import { findAllPlatforms_findAllPlatforms_items as Platform } from '__generatedTypes__/findAllPlatforms'
import { useImageUpload } from 'utils/saveImageS3'
import { BlogPostType, Form, VALIDATE_TABLE_OF_CONTENT_OPTIONS } from 'pagesNew/SuccessStory/Create/Form'
import * as classes from 'pagesNew/SuccessStory/Create/styles'
import { FormValues } from 'pagesNew/SuccessStory/Create/types'
import { handleServerErrors, prepareHtmlForSubmit } from 'pagesNew/SuccessStory/Create'
import { CREATE_STRATEGY } from 'queries/strategies'
import { createStrategy, createStrategyVariables } from '__generatedTypes__/createStrategy'
import { cmsRoutes, useLink } from 'routes/cmsRoutes'
import { QUERY_PARAM as SUBJECT_QUERY_PARAM } from 'pagesNew/Strategies/List'
import { toast } from 'utils/toast'
import { CARD_GROUP_QUERY_PARAM } from '../List/components/GroupCards'

type Props = {
  platform: Platform
}

export const Create = withPlatformCheck(({ platform }: Props) => {
  const uploadImage = useImageUpload()
  const { navigate } = useLink()
  const [searchParams] = useSearchParams()

  const [createStudyGuide] = useMutation<createStrategy, createStrategyVariables>(CREATE_STRATEGY)

  const handleSubmit = async (values: FormValues) => {
    try {
      const {
        title,
        description = '',
        featuredArticleDescription,
        slug,
        withWhiteImageBG = false,
        strategyGroup,
        membershipLevel,
      } = values

      const tableOfContent = getTableOfContent(values.contentAsHtml)
      const { error } = validateTableOfContent(tableOfContent, VALIDATE_TABLE_OF_CONTENT_OPTIONS)

      if (error) {
        return
      }

      const htmlContent = await prepareHtmlForSubmit(values)

      const coverImageFile = values.coverImage?.[0]?.file

      const coverImage = await (coverImageFile
        ? uploadImage(coverImageFile, BLOG_POST_MEDIA_PATH_S3)
        : Promise.resolve(''))

      const authorId = values.author[0].id || ''

      await createStudyGuide({
        variables: {
          createStrategyInput: {
            title,
            description,
            featuredArticleDescription,
            slug,
            author: {
              id: authorId,
            },
            content: htmlContent,
            withWhiteImageBG,
            strategyGroup: {
              id: String(strategyGroup?.id),
            },
            coverImage,
            tableOfContent,
            membershipLevel: membershipLevel?.id,
          },
        },
      })

      navigate(
        cmsRoutes.STRATEGIES,
        {},
        {
          searchParams: createSearchParams({
            [SUBJECT_QUERY_PARAM]: String(searchParams.get(SUBJECT_QUERY_PARAM)),
            [CARD_GROUP_QUERY_PARAM]: String(searchParams.get(CARD_GROUP_QUERY_PARAM)),
          }),
        }
      )
      toast.success('Strategy created successfully')
    } catch (error: unknown) {
      toast.error('Failed to create strategy')

      return handleServerErrors(error)
    }
  }

  return (
    <section css={classes.container}>
      <Form blogPostType={BlogPostType.PatStrategy} platform={platform} onSubmit={handleSubmit} />
    </section>
  )
})
